import { FormFieldContext } from '#app/components/FormField/context.js';
import { TextFieldContext } from './context.js';
import { type StyledTextFieldInputProps } from './styled/StyledTextFieldInput.js';
import { TextFieldInput } from './TextFieldInput.js';
import { AddIcon, DividerIcon } from '@contra/icons/icons';
import {
  type ComponentPropsWithoutRef,
  forwardRef,
  type Ref,
  useContext,
  useState,
} from 'react';
import { css, styled } from 'styled-components';

// Hide default arrows
const StyledTextFieldNumericInput = styled(TextFieldInput)`
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
    border: none;
  }

  &[type='number'] {
    -moz-appearance: textfield;
    border: none;
  }
`;

const StyledNumberControls = styled.button`
  ${({ theme }) => css`
    color: ${theme.colorsV2.black};
    cursor: pointer;
    align-self: end;
  `}
`;

const StyledNumberControlsSeparator = styled.div`
  ${({ theme }) => css`
    font-size: 24px;
    color: ${theme.colorsV2.gray40};
    font-weight: 100;
  `}
`;

const NumericInputContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin: 12px;
`;

const StyledNumericControlsContainer = styled.div`
  display: flex;
  margin: 4px;
  gap: 4px;
  align-items: center;
  justify-content: center;
`;

type TextFieldInputProps = ComponentPropsWithoutRef<'input'> &
  StyledTextFieldInputProps & {
    readonly onValueChange: (value: number) => void;
    readonly value: number | undefined;
  };

export const TextFieldNumericInput = forwardRef(
  (
    { onValueChange, value, ...props }: TextFieldInputProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    const { hasError } = useContext(TextFieldContext);
    const { errorAriaId } = useContext(FormFieldContext);
    const [inputValue, setInputValue] = useState<number>(value ?? 1);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = Number.parseInt(event.target.value, 10);
      onValueChange(newValue);
    };

    const handleIncrement = () => {
      const newValue = inputValue + 1;
      setInputValue(newValue);
      onValueChange(newValue);
    };

    const handleDecrement = () => {
      const newValue = inputValue - 1;
      setInputValue(newValue);
      onValueChange(newValue);
    };

    return (
      <NumericInputContainer>
        <StyledTextFieldNumericInput
          aria-errormessage={errorAriaId}
          // We only want aria-invalid in the dom when true.
          aria-invalid={hasError || undefined}
          onChange={handleInputChange}
          ref={ref}
          type="number"
          value={inputValue}
          {...props}
        />
        <StyledNumericControlsContainer>
          <StyledNumberControls onClick={handleDecrement}>
            <DividerIcon size={20} />
          </StyledNumberControls>
          <StyledNumberControlsSeparator>|</StyledNumberControlsSeparator>
          <StyledNumberControls onClick={handleIncrement}>
            <AddIcon size={20} />
          </StyledNumberControls>
        </StyledNumericControlsContainer>
      </NumericInputContainer>
    );
  },
);

TextFieldNumericInput.displayName = 'TextField.NumericInput';
